/**
 * validation.js - Functions for validating a puzzle grid
 */

// Import necessary utility functions
import { getRowWord, getColumnWord, cellsMatch, countGridLetters } from '../utils/gridHelpers.js';

// No longer extending SqudleCore, export functions directly

/**
 * Check if all rows in a grid form valid words
 * @param {Array<Array<string>>} grid - The grid to check
 * @param {Set<string>} validWordSet - Set of valid words
 * @returns {boolean} True if all rows are valid words
 */
export function areAllRowsValid(grid, validWordSet) { // Export function
  if (!grid || !validWordSet) return false;
  for (let row = 0; row < grid.length; row++) {
    const wordString = getRowWord(grid, row); // Use imported function
    if (!validWordSet.has(wordString)) {
      return false;
    }
  }
  return true;
};

/**
 * Check if all columns in a grid form valid words
 * @param {Array<Array<string>>} grid - The grid to check
 * @param {Set<string>} validWordSet - Set of valid words
 * @returns {boolean} True if all columns are valid words
 */
export function areAllColumnsValid(grid, validWordSet) { // Export function
  if (!grid || !validWordSet) return false;
  const gridSize = grid.length;
  if (gridSize === 0) return true; // Empty grid is valid?

  for (let col = 0; col < gridSize; col++) {
    const columnWord = getColumnWord(grid, col); // Use imported function
    if (!validWordSet.has(columnWord)) {
      return false;
    }
  }
  return true;
};

/**
 * Check if all words (rows and columns) in the grid are unique
 * @param {Array<Array<string>>} grid - The grid to check
 * @returns {boolean} True if all words are unique
 */
export function allWordsAreUnique(grid) { // Export function
  if (!grid) return true;
  const gridSize = grid.length;
  const allWords = new Set();
  let wordCount = 0;

  // Check rows
  for (let row = 0; row < gridSize; row++) {
    const rowWord = getRowWord(grid, row); // Use imported function
    if (rowWord.length === gridSize) { // Only count complete words
        allWords.add(rowWord);
        wordCount++;
    }
  }

  // Check columns
  for (let col = 0; col < gridSize; col++) {
    const columnWord = getColumnWord(grid, col); // Use imported function
     if (columnWord.length === gridSize) { // Only count complete words
        allWords.add(columnWord);
        wordCount++;
     }
  }

  // If the number of unique words in the set is less than the total number
  // of rows + columns, there must have been duplicates.
  return allWords.size === wordCount;
}

/**
 * Check if any letters are in their correct positions
 * @param {Array<Array<string>>} currentGrid - Current grid
 * @param {Array<Array<string>>} solutionGrid - Solution grid
 * @returns {boolean} True if any letters are in correct positions
 */
export function hasCorrectPositions(currentGrid, solutionGrid) { // Export function
  if (!currentGrid || !solutionGrid || currentGrid.length !== solutionGrid.length) return false;
  const gridSize = currentGrid.length;
  for (let row = 0; row < gridSize; row++) {
    if (!currentGrid[row] || !solutionGrid[row] || currentGrid[row].length !== solutionGrid[row].length) return false; // Row length mismatch
    for (let col = 0; col < gridSize; col++) {
      if (cellsMatch(currentGrid, solutionGrid, row, col)) { // Use imported function
        return true;
      }
    }
  }
  return false;
};

/**
 * Get the validation state of each cell in the grid.
 * Values: 0 = no match, 1 = yellow (row), 2 = yellow (column), 3 = yellow (both), 4 = green
 * @param {Array<Array<string>>} currentGrid - Current grid
 * @param {Array<Array<string>>} solutionGrid - Solution grid
 * @returns {Array<Array<number>>} Grid with validation states
 */
export function getValidationState(currentGrid, solutionGrid) { // Export function
  if (!currentGrid || !solutionGrid || currentGrid.length !== solutionGrid.length) {
      console.error("getValidationState: Invalid grids provided.");
      const size = currentGrid?.length || 0;
      return Array(size).fill().map(() => Array(size).fill(0)); // Return empty state
  }
  const gridSize = currentGrid.length;
  const validationGrid = Array(gridSize).fill().map(() => Array(gridSize).fill(0));

  // --- Data Structures for Yellow Logic ---
  // Count available letters in solution rows/cols (excluding those used for green)
  const solutionRowLetterCounts = Array(gridSize).fill().map(() => new Map());
  const solutionColLetterCounts = Array(gridSize).fill().map(() => new Map());

  // --- Step 1: Mark Green Tiles & Count Available Letters ---
  for (let r = 0; r < gridSize; r++) {
     if (!currentGrid[r] || !solutionGrid[r] || currentGrid[r].length !== gridSize || solutionGrid[r].length !== gridSize) {
         console.error(`getValidationState: Invalid row structure at index ${r}.`);
         return Array(gridSize).fill().map(() => Array(gridSize).fill(0)); // Return empty state on error
     }
    for (let c = 0; c < gridSize; c++) {
      const solutionLetter = solutionGrid[r][c];
      if (currentGrid[r][c] === solutionLetter) {
        validationGrid[r][c] = 4; // Green
      } else {
        // Increment counts for non-green letters in the solution
        solutionRowLetterCounts[r].set(solutionLetter, (solutionRowLetterCounts[r].get(solutionLetter) || 0) + 1);
        solutionColLetterCounts[c].set(solutionLetter, (solutionColLetterCounts[c].get(solutionLetter) || 0) + 1);
      }
    }
  }

  // --- Step 2: Mark Yellow Tiles ---
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      // Skip green tiles
      if (validationGrid[r][c] === 4) continue;

      const currentLetter = currentGrid[r][c];
      let yellowState = 0; // 0: None, 1: Row, 2: Col, 3: Both

      // Check if this letter exists in the solution for this row
      if ((solutionRowLetterCounts[r].get(currentLetter) || 0) > 0) {
        yellowState += 1; // Mark yellow for row
      }

      // Check if this letter exists in the solution for this column
      if ((solutionColLetterCounts[c].get(currentLetter) || 0) > 0) {
        yellowState += 2; // Mark yellow for column
      }

      // Assign the final yellow state (1, 2, or 3)
      if (yellowState > 0) {
        validationGrid[r][c] = yellowState;
      }
    }
  }

  return validationGrid;
}

/**
 * Verify that all required letters from the solution are present in the current grid
 * with the same frequency.
 * @param {Array<Array<string>>} currentGrid - Current grid
 * @param {Array<Array<string>>} solutionGrid - Solution grid
 * @returns {boolean} True if the letter balance is correct
 */
export function verifyLetterBalance(currentGrid, solutionGrid) { // Export function
  if (!currentGrid || !solutionGrid || currentGrid.length !== solutionGrid.length) return false;

  // Use imported utility function
  const solutionLetters = countGridLetters(solutionGrid);
  const currentLetters = countGridLetters(currentGrid);

  // Check if sizes match first
  if (solutionLetters.size !== currentLetters.size) {
    return false;
  }

  // Check if letter counts match for every letter in the solution
  for (const [letter, count] of solutionLetters.entries()) {
    if (currentLetters.get(letter) !== count) {
      return false;
    }
  }

  return true;
};

/**
 * Helper to validate the structure and content of a generated/fallback grid.
 * @param {Array<Array<string>>} gridToValidate - The grid to check.
 * @param {Set<string>} validWordSet - The set of valid words.
 * @returns {boolean} True if the grid is structurally sound and words are valid/unique.
 */
export function validateGridStructure(gridToValidate, validWordSet) {
   const gridSize = gridToValidate?.length;
   if (!gridToValidate || !Array.isArray(gridToValidate) || !gridSize || gridSize < 4) {
       console.error("Validation Error: Grid structure invalid (null, not array, or invalid size).");
       return false;
   }
   let isComplete = true; // Track if grid is fully populated
   for (let r = 0; r < gridSize; r++) {
       if (!gridToValidate[r] || !Array.isArray(gridToValidate[r]) || gridToValidate[r].length !== gridSize) {
           console.error(`Validation Error: Row ${r} structure invalid (null, not array, or wrong length).`);
           return false;
       }
       for (let c = 0; c < gridSize; c++) {
           const cell = gridToValidate[r][c];
           if (typeof cell !== 'string' || cell.length > 1) {
               console.error(`Validation Error: Cell (${r},${c}) is invalid type/length: '${cell}'`);
               return false;
           }
           if (cell.length === 0) {
               isComplete = false; // Mark as incomplete if any cell is empty
           } else if (!/^[A-Z]$/.test(cell)) { // Check for uppercase letter only if not empty
               console.error(`Validation Error: Cell (${r},${c}) is not a single uppercase letter: '${cell}'`);
               return false;
           }
       }
   }

   // If grid is structurally okay but incomplete, it's invalid for a *solved* grid.
   if (!isComplete) {
       console.error("Validation Error: Grid is incomplete (contains empty cells).");
       return false;
   }

   // Check word validity and uniqueness using functions defined in this module
   const rowsValid = areAllRowsValid(gridToValidate, validWordSet);
   if (!rowsValid) {
       console.error("Validation Error: One or more rows are invalid words.");
       return false;
   }
   const colsValid = areAllColumnsValid(gridToValidate, validWordSet);
   if (!colsValid) {
       console.error("Validation Error: One or more columns are invalid words.");
       return false;
   }
   const uniqueValid = allWordsAreUnique(gridToValidate);
   if (!uniqueValid) {
       console.error("Validation Error: Grid contains duplicate row/column words.");
       return false;
   }

   return true; // Grid is valid
}
